<script>
import { mapActions } from "vuex";
import PageHeader from "@/components/page-header";
import UserForm from "@/components/users/user-form";
// grid imports
import ImageCell from "@/components/sharedCells/ImageCell";
import ActionCell from "@/components/usersSettings/ActionCell";
import { usersColumns } from "@/utils/dataSource";
import UserMerge from "@/components/usersSettings/UserMerge";
import store from "@/state/store";
import Multiselect from "vue-multiselect";
import AlertFoundActiveSession from "@/components/emergency/AlertFoundActiveSession";
import { rfidFormatterMixin } from "@/utils/mixins/rfidFormatterMixin";
import usersService from "@/services/users";

export default {
  title: "Duplication Control Board",
  name: "UsersSettings",
  mixins: [rfidFormatterMixin],
  components: {
    PageHeader,
    UserForm,
    ImageCell,
    ActionCell,
    UserMerge,
    Multiselect,
    AlertFoundActiveSession,
  },
  data() {
    return {
      title: this.$t('duplicationControlBoard.title'),
      isAddModal: false,
      // columns for export
      statusOptions: ["Active", "Deactivate"],
      systemUserOptions: ["Yes", "No"],
      usersColumns: usersColumns,
      showDetailIcon: false,
      reasonOptions: [
        { id: 0, name: this.$t("common.fullName") },
        { id: 1, name: this.$t("common.userId") },
        { id: 2, name: this.$t("common.RFID") },
        { id: 3, name: this.$t("common.faceId") },
        { id: 4, name: this.$t("common.email1") },
      ],
      reasons: null,
      duplicatedUsers: [],
    };
  },
  watch: {
    async reasons(reasons) {
      this.$bvModal.show("loader");
      if (reasons && reasons.length == 0) {
        this.reasons = null;
      }

      let payload = [];
      payload = reasons?.map((reason) => reason.id) || [];

      await this.fetchDuplicates({ filterBy: payload });
    },
  },
  methods: {
    ...mapActions("modal", ["onResetModal"]),
    ...mapActions("grid", ["resetTableHeaders", "resetFilterValues"]),
    async fetchDuplicates(payload) {
      try {
        let entity = store.getters["authfack/currentEntity"];
        const { data } = await usersService.getDuplicatedUsers(payload);
        this.duplicatedUsers = data;
        this.duplicatedUsers = this.duplicatedUsers?.map((user, i) => {
          let imageProfile = user.image;
          if (imageProfile !== null) {
            imageProfile = imageProfile?.replace("entity_id", entity.id);
          }
          return {
            Image: imageProfile,
            FirstName: user.first_name,
            LastName: user.last_name,
            UserType: user.user_type_name,
            ID: user.user_id,
            Email: user.email,
            RFID: user.rf_ids?.length ? user.rf_ids : null,
            FaceID: user.faceID ? user.faceID : "-",
            Reason: this.checkReason(user.reason),
            Data: user,
            children: user.children,
            key: i,
          };
        });
      } catch (e) {
        console.error("Could not fetch getDuplicatedUsers", e);
      } finally {
        this.$bvModal.hide("loader");
      }
    },
    setResetModal() {
      this.$bvModal.hide("add-edit-user");
    },
    hideModalTrigger() {
      this.$store.dispatch("modal/setData", null);
      this.isAddModal = false;
    },
    onRowClicked() {
      return "";
    },
    toggle(row) {
      row.isCollapse = !row.isCollapse;
      this.$refs.table.toggleDetails(row);
    },
    checkReason(reasonKey) {
      // full_name => 0
      // user_id =>1
      // rfid_number => 2
      // faceID=>3
      // email=>4

      switch (reasonKey) {
        case 0:
          return this.$t('duplicationControlBoard.fullNameDuplicated');
        case 1:
          return this.$t('duplicationControlBoard.userIdDuplicated');
        case 2:
          return this.$t('duplicationControlBoard.rfidNumberDuplicated');
        case 3:
          return this.$t('duplicationControlBoard.faceIdDuplicated');
        case 4:
          return this.$t('duplicationControlBoard.emailsDuplicated');
        default:
          "";
      }
    },
  },
  async mounted() {
    this.$bvModal.show("loader");
    await this.fetchDuplicates({ filterBy: [] });
  },
  destroyed() {
    this.resetTableHeaders();
    this.resetFilterValues();
    this.onResetModal();
  },
};
</script>

<template>
  <div class="main-page-container">
    <AlertFoundActiveSession />
    <PageHeader
      :title="title"
      className="d-flex align-items-center justify-content-between"
    >
    </PageHeader>
    <!-- user merge -->
    <b-modal id="userMerge" hide-footer hide-header size="lg">
      <UserMerge />
    </b-modal>
    <!-- update user  -->
    <b-modal
      id="add-edit-user"
      class="popover-modal"
      hide-header
      hide-footer
      no-close-on-esc
      ref="add-edit-user"
      @hide="hideModalTrigger"
    >
      <slot name="modal-header">
        <div class="modal-header flex-row">
          <h5 class="modal-title">
            <slot name="modal-title">
              {{ isAddModal ? "Add user" : "Edit user" }}
            </slot>
          </h5>
          <button
            type="button"
            class="header-close"
            aria-label="headerCloseLabel"
            @click="setResetModal"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      </slot>
      <UserForm :data="$store.state.modal.data" />
    </b-modal>
    <b-modal
      id="loader"
      class="popover-modal"
      hide-header
      hide-footer
      no-close-on-esc
      no-close-on-backdrop
      :scrollable="false"
    >
      <div class="dup-spinner spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </b-modal>
    <section class="tree-grid pt-3 px-2" style="background-color: white; border-radius: 8px">
      <!-- grid -->
      <b-table
        :data="duplicatedUsers"
        ref="table"
        detailed
        custom-detail-row
        :default-sort="['name', 'asc']"
        detail-key="key"
        :show-detail-icon="showDetailIcon"
        :checkable="false"
        :striped="false"
        :narrowed="false"
      >
        <template slot="empty">
          <h5 class="text-center my-2 text-muted">{{ $t('common.noMatchingData') }}</h5>
        </template>
        <!-- toggled arrow  -->
        <b-table-column
          field=""
          label=""
          width="50"
          :visible="true"
          v-slot="props"
        >
          <template>
            <div
              class="icon-wrapper"
              :class="props.row.isCollapse ? 'is-expanded' : ''"
            >
              <i
                v-if="props.row.children.length > 0"
                class="mdi toggle-icon mdi-chevron-right"
                @click="toggle(props.row)"
              ></i>
            </div>
          </template>
        </b-table-column>
        <!-- image -->
        <b-table-column field="Image" :visible="true" label="" v-slot="props">
          <ImageCell
            :image="props.row.Image"
            :name="props.row.FirstName.charAt(0)"
          />
        </b-table-column>
        <!-- first name -->
        <b-table-column
          field="FirstName"
          :visible="true"
          :label="$t('common.firstName')"
          sortable
          searchable
        >
          <template slot="searchable" slot-scope="props">
            <b-input
              class="user-search-input"
              v-model="props.filters[props.column.field]"
              :placeholder="$t('common.filter')"
              size="md"
            />
          </template>
          <template v-slot="props">
            {{ props.row.FirstName }}
          </template>
        </b-table-column>
        <!-- last name -->
        <b-table-column
          field="LastName"
          :label="$t('common.lastName')"
          sortable
          searchable
          :visible="true"
        >
          <template slot="searchable" slot-scope="props">
            <b-input
              class="user-search-input"
              v-model="props.filters[props.column.field]"
              :placeholder="$t('common.filter')"
              size="md"
            />
          </template>
          <template v-slot="props">
            {{ props.row.LastName }}
          </template>
        </b-table-column>
        <!-- user type -->
        <b-table-column
          field="UserType"
          :label="$t('common.userType')"
          sortable
          searchable
          :visible="true"
        >
          <template slot="searchable" slot-scope="props">
            <b-input
              class="user-search-input"
              v-model="props.filters[props.column.field]"
              :placeholder="$t('common.filter')"
              size="md"
            />
          </template>
          <template v-slot="props">
            {{ props.row.UserType }}
          </template>
        </b-table-column>
        <!-- id -->
        <b-table-column
          field="ID"
          :label="$t('common.id')"
          sortable
          searchable
          :visible="true"
        >
          <template slot="searchable" slot-scope="props">
            <b-input
              class="user-search-input"
              v-model="props.filters[props.column.field]"
              :placeholder="$t('common.filter')"
              size="md"
            />
          </template>
          <template v-slot="props">
            {{ props.row.ID }}
          </template>
        </b-table-column>
        <!-- email -->
        <b-table-column
          field="Email"
          :label="$t('common.emailAddress')"
          sortable
          searchable
          :visible="true"
        >
          <template slot="searchable" slot-scope="props">
            <b-input
              class="user-search-input"
              v-model="props.filters[props.column.field]"
              :placeholder="$t('common.filter')"
              size="md"
            />
          </template>
          <template v-slot="props">
            {{ props.row.Email }}
          </template>
        </b-table-column>
        <!-- RFID -->
        <b-table-column
          field="RFID"
          :visible="true"
          :label="$t('common.RFID')"
          style="min-width: 150px"
        >
          <!-- <template slot="searchable" slot-scope="props">
            <b-input
              class="user-search-input"
              v-model="props.filters[props.column.field]"
              :placeholder="$t('common.filter')"
              size="md"
            />
          </template> -->
          <template v-slot="props">
            {{ props.row.RFID ? rfidFormatter(props.row.RFID) : "-" }}
          </template>
        </b-table-column>
        <!-- FACE ID  -->
        <b-table-column
          field="FaceID"
          :visible="true"
          :label="$t('common.faceId')"
          sortable
          searchable
          width="100px"
        >
          <template slot="searchable" slot-scope="props">
            <b-input
              class="user-search-input"
              v-model="props.filters[props.column.field]"
              :placeholder="$t('common.filter')"
              size="md"
            />
          </template>
          <template v-slot="props">
            {{ props.row.FaceID }}
          </template>
        </b-table-column>
        <!-- reason -->
        <b-table-column
          field="Reason"
          :visible="true"
          :label="$t('common.reason')"
          sortable
          searchable
        >
          <template slot="searchable">
            <multiselect
              class="no-wrap"
              v-if="true"
              v-model="reasons"
              :options="reasonOptions"
              :placeholder="$t('common.filter')"
              multiple
              trackBy="name"
              label="name"
              :show-labels="false"
              :hideSelected="true"
              style="min-width: 120px"
            />
          </template>
          <template v-slot="props">
            <span class="font-weight-normal text-danger no-wrap">
              {{ props.row.Reason }}
            </span>
          </template>
        </b-table-column>
        <!-- actions -->
        <b-table-column field="Actions" :label="$t('common.grid.actions')">
          <template v-slot="props">
            <ActionCell type="parent" :data="props.row.Data" />
          </template>
        </b-table-column>
        <!-- details  -->
        <template slot="detail" slot-scope="props">
          <tr v-for="item in props.row.children" :key="item.id">
            <td></td>
            <!-- image  -->
            <td v-show="true">
              <ImageCell
                :image="item.image"
                :name="item.first_name.charAt(0)"
              />
            </td>
            <!-- first_name  -->
            <td v-show="true">{{ item.first_name }}</td>
            <!-- last name  -->
            <td v-show="true">{{ item.last_name }}</td>
            <!-- last name  -->
            <td v-show="true">{{ item.user_type_name }}</td>
            <!-- id  -->
            <td v-show="true">{{ item.user_id }}</td>
            <!-- email  -->
            <td v-show="true">{{ item.email }}</td>
            <!-- rfid  -->
            <td v-show="true">{{ item.rfid_number }}</td>
            <!-- faceId  -->
            <td v-show="true">{{ item.faceID }}</td>
            <!-- reason  -->
            <td> </td>
            <td v-show="true">
              <ActionCell :data="item" />
            </td>
          </tr>
        </template>
      </b-table>
    </section>
  </div>
</template>
<style lang="scss" scoped>
.dup-spinner {
  width: 30px;
  height: 30px;
  align-items: center;
  text-align: center;
  margin-inline-start: 50%;
}

.tree-grid {
  .toggle-icon {
    cursor: pointer;
    font-size: 1.5rem;
    line-height: 1.5rem;
  }
  .icon-warpper {
    display: flex;
    justify-content: center;
    height: 1.5rem;
    width: 1.5rem;
  }
  .is-expanded {
    transform: rotate(90deg);
    transition: transform 150ms ease-out, opacity 86ms ease-out,
      -webkit-transform 150ms ease-out;
  }
}
</style>

<style lang="scss">
button.mr-2 {
  margin-inline-end: 0.5rem;
}
.dropdown-menu {
  text-align: start !important;
}
:dir(rtl) button.dropdown-item {
  padding-right: 1rem !important;
}
:dir(rtl) .dropdown-toggle {
  width: 120px;
}
div#loader___BV_modal_content_ {
  margin-top: 75%;
  background: transparent;
  border: none;
  box-shadow: none;
  margin-inline-start: 10%;
}

.tree-grid {
  .table-wrapper {
    height: calc(100vh - 237px);
  }
  .multiselect__tags {
    display: grid;
  }
}
</style>
